import mindMapImage from '../assets/tutorial/mind-map.jpg';
import yellowCrossCornerImage from '../assets/tutorial/yellow-cross-corner.jpg';
import yellowCrossLineImage from '../assets/tutorial/yellow-cross-line.jpg';
import fishLeftImage from '../assets/tutorial/fish-left.jpg';
import fishRightImage from '../assets/tutorial/fish-right.jpg';

export const tutorialContent = Object.freeze({
  title: '魔方教程',
  introduction: Object.freeze({
    id: 'introduction',
    title: '介绍',
    blocks: Object.freeze([
      Object.freeze({ type: 'paragraph', text: '魔方是一种经典的益智类玩具，由多个可以旋转的方块组成。玩家需要通过转动不同的面，使每个面的颜色最终恢复为完整状态。' }),
      Object.freeze({ type: 'paragraph', text: '## 历史由来' }),
      Object.freeze({ type: 'paragraph', text: '魔方最早由匈牙利建筑学教授、雕塑家厄尔诺·鲁比克（Ernő Rubik）于1974年发明。' }),
      Object.freeze({ type: 'paragraph', text: '鲁比克最初制作这个立方体并不是为了设计玩具，而是为了帮助学生理解三维空间结构。在设计过程中，他发现这个立方体经过旋转后会产生大量不同的组合状态，而恢复原状并不容易。' }),
      Object.freeze({ type: 'paragraph', text: '1977年，魔方开始在匈牙利以“魔方”（Bűvös kocka，意为“魔法方块”）的名称销售。1980年，该产品被引入国际市场，并更名为“Rubik\'s Cube（鲁比克魔方）”，随后迅速风靡全球。' }),
      Object.freeze({ type: 'paragraph', text: '自推出以来，魔方成为世界上最受欢迎的益智玩具之一，并发展出了各种不同阶数、不同结构的魔方。如今，魔方不仅是一种娱乐工具，也成为竞速运动项目，世界魔方协会（WCA）每年都会举办大量正式比赛。' }),
      Object.freeze({ type: 'paragraph', text: '## 魔方的结构' }),
      Object.freeze({ type: 'paragraph', text: '一个标准三阶魔方包含：' }),
      Object.freeze({ type: 'paragraph', text: '- 6 个中心块：位于每个面的中心，决定该面的最终颜色。\n- 12 个棱块：位于两个颜色交界的位置。\n- 8 个角块：位于三个颜色交界的位置。' }),
      Object.freeze({ type: 'paragraph', text: '中心块不会改变相对位置，因此可以通过中心块判断每个面的目标颜色。' }),
      Object.freeze({ type: 'paragraph', text: '## 魔方的阶数' }),
      Object.freeze({ type: 'paragraph', text: '根据每个面的方块数量，魔方可以分为不同阶数：' }),
      Object.freeze({ type: 'paragraph', text: '- 二阶魔方：2×2×2，没有棱块。\n- 三阶魔方：3×3×3，是最经典、最常见的魔方。\n- 四阶魔方：4×4×4，结构更加复杂，需要处理特殊情况。\n- 五阶魔方：5×5×5，拥有更多方块和变化。' }),
      Object.freeze({ type: 'paragraph', text: '## 学习魔方' }),
      Object.freeze({ type: 'paragraph', text: '学习魔方通常需要经历：' }),
      Object.freeze({ type: 'paragraph', text: '1. 认识魔方结构和基本转动方式；\n2. 学习基础还原方法；\n3. 熟悉常用公式；\n4. 通过练习提高还原速度。' }),
      Object.freeze({ type: 'paragraph', text: '本教程来源于抖音博主 @黄泽浩_85（深圳魔方教练） (https://www.douyin.com/video/7531371509873626402)' }),
    ]),
  }),
  courses: Object.freeze([
    Object.freeze({
      id: '3x3',
      navigationTitle: '3阶魔方教学',
      title: '三阶魔方教学',
      available: true,
      blocks: Object.freeze([
        Object.freeze({ type: 'image', src: mindMapImage, alt: '思维导图' }),
      ]),
      chapters: Object.freeze([
        Object.freeze({
          id: '3x3-1',
          navigationTitle: '1. 黄心白十字架',
          title: '1. 黄心白十字架',
          dividerAfter: true,
          blocks: Object.freeze([
            Object.freeze({ type: 'paragraph', text: '完成黄色中心周围的白色十字。' }),
          ]),
        }),
        Object.freeze({
          id: '3x3-2',
          navigationTitle: '2. 白色小花变白色十字',
          title: '2. 白色小花变白色十字',
          dividerAfter: true,
          blocks: Object.freeze([
            Object.freeze({ type: 'paragraph', text: '下转两下得到白色十字架。' }),
          ]),
        }),
        Object.freeze({
          id: '3x3-3',
          navigationTitle: '3. 停小汽车 —— 还原第一层',
          title: '3. 停小汽车 ------ 还原第一层',
          dividerAfter: true,
          blocks: Object.freeze([
            Object.freeze({ type: 'paragraph', text: '①白色朝下，对应白色十字。' }),
            Object.freeze({ type: 'paragraph', text: '面左边有两个颜色（一定要符合白色另外两个面左右的位置），白色对齐自己的，左边左勾一下，右边右勾一下。' }),
            Object.freeze({ type: 'paragraph', text: '②白色面在上面，调到对应两边后，三次勾上回下。' }),
            Object.freeze({ type: 'paragraph', text: '③在最后一层的白面，做一次对应左右的勾上勾下就白色朝自己了。' }),
          ]),
        }),
        Object.freeze({
          id: '3x3-4',
          navigationTitle: '4. 停大卡车 —— 拼好第二层',
          title: '4. 停大卡车 ------ 拼好第二层',
          dividerAfter: true,
          blocks: Object.freeze([
            Object.freeze({ type: 'paragraph', text: '①白色面在下面，第一层不是白心的中间边，下面对齐，右上面是红色，去右边对应左右的勾上下，再转到有白色面的一边（左上角），再做一次对应左右的勾上下。' }),
            Object.freeze({ type: 'paragraph', text: '②如果第二层，把大车开出来，对应左右的勾上下，再找左上角白色面，再次勾上下到第二层，重复第一步。' }),
          ]),
        }),
        Object.freeze({
          id: '3x3-5',
          navigationTitle: '5. 拼好第三层中间的黄色十字架',
          title: '5. 拼好第三层中间的黄色十字架',
          dividerAfter: true,
          blocks: Object.freeze([
            Object.freeze({ type: 'image', src: yellowCrossCornerImage, alt: '黄色十字-拐角' }),
            Object.freeze({ type: 'image', src: yellowCrossLineImage, alt: '黄色十字-一字' }),
            Object.freeze({ type: 'paragraph', text: '点一下拐角→一字' }),
            Object.freeze({ type: 'paragraph', text: '拐角（一放在左上角）用对应公式。' }),
            Object.freeze({ type: 'paragraph', text: '一字（横过来）用对应公式。' }),
          ]),
        }),
        Object.freeze({
          id: '3x3-6',
          navigationTitle: '6. 复原黄色面',
          title: '6. 复原黄色面',
          dividerAfter: true,
          blocks: Object.freeze([
            Object.freeze({ type: 'image', src: fishRightImage, alt: '右小鱼' }),
            Object.freeze({ type: 'image', src: fishLeftImage, alt: '左小鱼' }),
            Object.freeze({ type: 'paragraph', text: '①缺三个角→缺小鱼。' }),
            Object.freeze({ type: 'paragraph', text: '判断左右黄色面的方法：左上/右上有黄色就是对应左右的小鱼。' }),
            Object.freeze({ type: 'paragraph', text: '用对应左右小鱼的公式。' }),
            Object.freeze({ type: 'paragraph', text: '②缺两个黄色（左手食指后面摸到黄色）做右小鱼→变成缺三个角，重复第一步。' }),
            Object.freeze({ type: 'paragraph', text: '③缺四个黄色（按照右边做右小鱼→变成缺三个角）重复第一步。' }),
          ]),
        }),
        Object.freeze({
          id: '3x3-7',
          navigationTitle: '7. 找眼睛',
          title: '7. 找眼睛',
          dividerAfter: true,
          blocks: Object.freeze([
            Object.freeze({ type: 'paragraph', text: '把第一层全部变成眼睛。' }),
            Object.freeze({ type: 'paragraph', text: '眼睛（两边一样中间不同三个都一样）。' }),
            Object.freeze({ type: 'paragraph', text: '①一双眼睛（放左边）→一字→拐角。' }),
            Object.freeze({ type: 'paragraph', text: '②没有眼睛→拐角→一字。' }),
          ]),
        }),
        Object.freeze({
          id: '3x3-8',
          navigationTitle: '8. 全部复原',
          title: '8. 全部复原',
          blocks: Object.freeze([
            Object.freeze({ type: 'paragraph', text: '①3眼睛→一样的把东西对齐白色黄色面朝上，后面的两个小鱼，顺时针做右边，逆时针做左边。' }),
            Object.freeze({ type: 'paragraph', text: '②四个眼睛→做一遍左右鱼。' }),
          ]),
        }),
      ]),
    }),
    Object.freeze({ id: '4x4', navigationTitle: '4阶魔方教学', available: false, chapters: Object.freeze([]) }),
    Object.freeze({ id: '5x5', navigationTitle: '5阶魔方教学', available: false, chapters: Object.freeze([]) }),
  ]),
});
